// Unpublished Work © 2023-2024 Deere & Company

import { Option, Select, useToastProvider } from "@deere/fuel-react";
import { AddComment, Close } from "@mui/icons-material";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  Radio,
  RadioGroup,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import dayjs, { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import {
  ChangeEvent,
  forwardRef,
  useContext,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

import { DealerDiscount } from "../../../../api/dealer/types";
import { UpdateShipToAddressResponse } from "../../../../api/xtremeBff/xtremeBffInterfaces";
import { ConfigContext } from "../../../../context/ConfigContext";
import { DealerAccountContext } from "../../../../context/DealerAccountContext";
import { FeatureToggleContextV2 } from "../../../../context/FeatureToggleContextV2/FeatureToggleContextV2";
import { RBACAccessContext } from "../../../../context/RBACAccessContext/RBACAccessContext";
import {
  UserContext,
  UserDetails,
} from "../../../../context/UserContext/UserContext";
import i18n from "../../../../i18n/i18n";
import { IPromotion } from "../../../../types";
import { formatDate } from "../../../../utils/formatters";
import {
  ADDITIONAL_FIELDS,
  CATEGORIES,
  TOGGLES,
} from "../../../featureToggles/constants";
import { convertAddressToTitleCase } from "../../Shell/utils";
import {
  Order,
  OrderClient,
  OrderServiceType,
  OrderStatus,
  OrderType,
} from "../../types";
import { createJDPointOrderLink, getMaxDate } from "../../utils";
import DealerAddress from "../DealerAddress/DealerAddress";
import EstimatedCost from "../EstimatedCost/EstimatedCost";
import InvalidAddressDialog from "../InvalidAddressModal/InvalidAddressDialog";
import ResourceLink from "../ResourceLink/ResourceLink";
import OrderStatusBadge from "../StatusBadge/OrderStatusBadge";
import OrderSummaryHeaderLineOne from "./components/OrderSummaryHeaderLineOne/OrderSummaryHeaderLineOne";
import PromotionSelect from "./components/PromotionSelect/PromotionSelect";
import ReadonlyPromotionDescriptionBlock from "./components/ReadonlyPromotionDescriptionBlock/ReadonlyPromotionDescriptionBlock";
import ReadonlyPromotionIdBlock from "./components/ReadonlyPromotionIdBlock/ReadonlyPromotionIdBlock";
import ReadonlyRequestedShipMonthBlock from "./components/ReadonlyRequestedShipMonthBlock/ReadonlyRequestedShipMonthBlock";
import RequestedShipMonthSelector from "./components/RequestedShipMonthSelector/RequestedShipMonthSelector";
import { headingSx } from "./styles";

dayjs.extend(utc);

export type OrderHeaderDelta = Required<
  Pick<
    Order,
    | "orderServiceType"
    | "orderConsolidation"
    | "dealerReference"
    | "orderNotes"
    | "discount"
    | "discountDescription"
  >
>;

export interface OrderHeaderProps {
  order: Order;
  save?: (delta: OrderHeaderDelta) => void;
  step?: number;
  dealerAccountId?: string;
  isAddressOverrideEditEnabled?: boolean;
  isAddressOverrideSelectionEnabled?: boolean;
  isAddressOverrideSelectionDisabled?: boolean;
  isCleanserAddressValidationEnabled?: boolean;
  isV2AddressValidationEnabled?: boolean;
  updateShipToAddress?: (res: UpdateShipToAddressResponse) => void;
  printing?: boolean;
  availablePromotions?: IPromotion[];
  promotion?: IPromotion;
  updatePromotion?: (value: IPromotion) => void;
  requestedShipMonth?: Dayjs;
  updateRequestedShipMonth?: (value: Dayjs | undefined) => void;
  expiredPromotionId?: string;
  dealerSelectionDiscountsSet?: DealerDiscount[];
  showRequestShipMonthWarning?: boolean;
  handleOnClickRequestShipMonth?: () => void;
  setWarningEditModel?: (value: boolean) => void;
}

const MIN_COLS_IN_PROMOTION_ORDER = 5;

export function canShowDealerAccountNumber(
  user: UserDetails | undefined,
  isRBACEnabled: boolean,
  selectedAccountsCount: number | undefined,
  orderStatus: string
): boolean {
  if (user === undefined) return false;
  return (
    orderStatus !== OrderStatus.UNSUBMITTED &&
    isRBACEnabled &&
    (selectedAccountsCount ?? 0) > 1
  );
}

export function isServiceEnabled(
  toggleValue: string,
  isFeatureToggleContextV2Enabled: boolean,
  isToggleEnabled: (featureName: string, toggleName: string) => boolean
) {
  if (isFeatureToggleContextV2Enabled) {
    return isToggleEnabled(CATEGORIES.EXTREME_UI, toggleValue);
  }
  return false;
}

const isToggleEnabledOnOrderType = (
  category: string,
  toggleName: string,
  orderType: OrderType,
  isFeatureToggleContextV2Enabled: boolean,
  isToggleEnabled: (featureName: string, toggleName: string) => boolean,
  additionalFieldsValues: (
    featureName: string,
    toggleName: string
  ) => Map<string, string[]> | undefined
) => {
  if (
    isFeatureToggleContextV2Enabled &&
    isToggleEnabled(category, toggleName)
  ) {
    const additionalFields = additionalFieldsValues(category, toggleName);
    return (
      (additionalFields?.get(ADDITIONAL_FIELDS.ORDER_TYPE)?.length === 0 ||
        additionalFields
          ?.get(ADDITIONAL_FIELDS.ORDER_TYPE)
          ?.includes(orderType && orderType.toLowerCase())) ??
      true
    );
  }

  return false;
};

const OrderHeader = forwardRef(
  (
    {
      order,
      save,
      step,
      dealerAccountId,
      isAddressOverrideEditEnabled = false,
      isAddressOverrideSelectionEnabled = false,
      isAddressOverrideSelectionDisabled = false,
      isCleanserAddressValidationEnabled = false,
      isV2AddressValidationEnabled = false,
      updateShipToAddress = () => {},
      printing,
      availablePromotions,
      promotion,
      updatePromotion,
      requestedShipMonth,
      updateRequestedShipMonth,
      expiredPromotionId = "",
      showRequestShipMonthWarning = false,
      handleOnClickRequestShipMonth = () => {},
      dealerSelectionDiscountsSet,
      setWarningEditModel,
    }: OrderHeaderProps,
    ref
  ) => {
    const theme = useTheme();
    const { t } = useTranslation("orders");
    const { dealerAccount, selectedAccounts } =
      useContext(DealerAccountContext);
    const { enqueueToast } = useToastProvider();
    const { isRBACEnabled } = useContext(RBACAccessContext);
    const user = useContext(UserContext);
    const {
      ENABLE_FEATURE_TOGGLE_CONTEXT_V2,
      JDPOINT_URL,
      ENABLE_DYNAMIC_PROMOTION_FEATURE_TOGGLE,
    } = useContext(ConfigContext);
    const { isToggleEnabled, additionalFieldsValues } = useContext(
      FeatureToggleContextV2
    );

    const accountId = dealerAccountId ?? dealerAccount?.id;

    const orderSubmitted =
      order.orderStatus !== OrderStatus.UNSUBMITTED || step === 2;

    const isOrderNotSubmittedAndInReviewPage =
      order.orderStatus === OrderStatus.UNSUBMITTED && step === 1;

    const isEntryPage =
      order.orderStatus === OrderStatus.UNSUBMITTED && step === 0;

    const isReviewAndSubmitPage =
      order.orderStatus === OrderStatus.UNSUBMITTED && step === 2;

    const showDealerAccountNumber = canShowDealerAccountNumber(
      user,
      isRBACEnabled,
      selectedAccounts?.length,
      order.orderStatus
    );

    const promotionOrderFlowEnabled =
      ENABLE_DYNAMIC_PROMOTION_FEATURE_TOGGLE &&
      isToggleEnabled(CATEGORIES.ORDER_SERVICE, TOGGLES.ENABLE_PROMOTION_ORDER);

    const showPromotionOptions =
      promotionOrderFlowEnabled &&
      order.orderType === OrderType.PROMOTION_ORDER;

    const showPromotionEntryOptions = step === 0 && showPromotionOptions;
    const showPromotionReadonlyOptions = step !== 0 && showPromotionOptions;

    const externalOrderNumberPresent = orderSubmitted && !!order.generatedFrom;
    const generatedByJDPoint =
      externalOrderNumberPresent && order.client === OrderClient.JDPOINT;

    const isOrderServiceEnabled = useMemo(
      () =>
        isToggleEnabledOnOrderType(
          CATEGORIES.EXTREME_UI,
          TOGGLES.ENABLE_ORDER_SERVICE_FIELD,
          order.orderType,
          ENABLE_FEATURE_TOGGLE_CONTEXT_V2,
          isToggleEnabled,
          additionalFieldsValues
        ),
      [accountId]
    );
    const isOrderConsolidationEnabled = useMemo(
      () =>
        isServiceEnabled(
          TOGGLES.DO_NOT_CONSOLIDATE_ORDER,
          ENABLE_FEATURE_TOGGLE_CONTEXT_V2,
          isToggleEnabled
        ),
      [accountId]
    );

    const isMaskShipToAddressEnabled =
      order.client === OrderClient.JDPOINT &&
      isServiceEnabled(
        TOGGLES.MASK_SHIP_TO_ADDRESS,
        ENABLE_FEATURE_TOGGLE_CONTEXT_V2,
        isToggleEnabled
      );

    const orderSummaryHeaderSx = {
      fontWeight: theme.tokens.FuelFontWeightSemibold,
      fontSize: theme.tokens.FuelFontSizeHeading2,
      color: theme.tokens.FuelColorTextPrimary,
    };
    const ordersSummaryContainerSx = {
      display: "block",
      border: "1px solid",
      borderColor: theme.tokens.FuelColorBorderLight,
      borderRadius: 2,
      pt: theme.tokens.FuelSpacing4,
      my: theme.tokens.FuelSpacing4,
      pb: 0,
    };

    const [orderServiceTypeValue, setOrderServiceTypeValue] = useState(
      order.orderServiceType
    );
    const handleChangeOrderServiceType = ({
      target: { value },
    }: ChangeEvent<HTMLInputElement>) =>
      setOrderServiceTypeValue(
        OrderServiceType[value as keyof typeof OrderServiceType]
      );

    const [orderConsolidationValue, setOrderConsolidationValue] =
      useState<boolean>(order.orderConsolidation);
    const handleChangeOrderConsolidation = () =>
      setOrderConsolidationValue(!orderConsolidationValue);

    const dealerReferenceMaxLength = 35;

    const [dealerReferenceValue, setDealerReferenceValue] = useState(
      order.dealerReference || null
    );
    const handleChangeDealerReference = (e: ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value.slice(0, dealerReferenceMaxLength);
      const remaining = dealerReferenceMaxLength - newValue.length;

      if (remaining >= 0) {
        if (remaining === dealerReferenceMaxLength) {
          setDealerReferenceValue(null);
        } else {
          setDealerReferenceValue(newValue);
        }
      }
    };

    const orderNotesMaxLength = 150;
    const [orderNotesValue, setOrderNotesValue] = useState(
      order.orderNotes || null
    );

    const [isOpen, setCommentOpenState] = useState<boolean>(false);
    const [comments, setComments] = useState<string>(orderNotesValue ?? "");

    const [isInvalidAddressDialogOpen, setIsInvalidAddressDialogOpen] =
      useState(false);

    const getApplicableDiscount = () => {
      if (!dealerSelectionDiscountsSet) return [];

      const discounts = dealerSelectionDiscountsSet
        .filter((dis) => dis.orderType === order.orderType)
        .sort((a, b) => a.sortSeq - b.sortSeq);

      if (!discounts) return [];

      return discounts;
    };

    const getDiscountDescription = (givenDiscount: string | null) =>
      applicableDiscounts.find((dis) => dis.discount === givenDiscount)
        ?.description ?? null;

    const applicableDiscounts = getApplicableDiscount();
    const defaultDiscount = applicableDiscounts[0]?.discount ?? null;

    const [dealerSelectedDiscount, setDealerSelectedDiscount] = useState({
      discount: order.discount || defaultDiscount,
      description:
        order.discountDescription || getDiscountDescription(defaultDiscount),
    });

    useImperativeHandle(ref, () => ({
      propagateDelta: () =>
        save &&
        save({
          dealerReference: dealerReferenceValue,
          orderNotes: orderNotesValue,
          orderConsolidation: orderConsolidationValue,
          orderServiceType: orderServiceTypeValue,
          discount: dealerSelectedDiscount.discount,
          discountDescription: dealerSelectedDiscount.description,
        }),
      getOrderServiceType: () => orderServiceTypeValue,
      isEdited: () =>
        order.orderServiceType !== orderServiceTypeValue ||
        order.orderConsolidation !== orderConsolidationValue ||
        order.dealerReference !== dealerReferenceValue ||
        order.orderNotes !== orderNotesValue,
      submitOrder: () => {
        if (!order.isShipToAddressValid) {
          setIsInvalidAddressDialogOpen(true);
          return false;
        }

        return true;
      },
    }));

    const getDealerReferenceField = () => (
      <TextField
        id="dealer-reference-input"
        placeholder={t("input.placeholder") || "Enter input here..."}
        value={dealerReferenceValue}
        inputProps={{
          "data-testid": "dealer-reference-input",
        }}
        type="text"
        size="medium"
        onChange={handleChangeDealerReference}
      />
    );

    const getOrderConsolidationToggleSwitch = () => (
      <FormControl component="fieldset">
        <FormGroup>
          <FormControlLabel
            control={
              <Switch
                id="order-consolidation-switch"
                data-testid="order-consolidation-switch"
                name="order-consolidation"
                onChange={handleChangeOrderConsolidation}
                checked={orderConsolidationValue}
                disabled={orderSubmitted}
                sx={{ marginTop: "0px", marginBottom: "0px" }}
              />
            }
            label=""
          />
        </FormGroup>
      </FormControl>
    );

    const getDeliveryOptionRadioGroup = () => (
      <FormControl
        component="fieldset"
        data-testid="delivery-option-radio-group"
        sx={{
          width: "max-content",
        }}
      >
        <RadioGroup
          id="order-service"
          aria-label="order-service"
          name="order-service"
          defaultValue={orderServiceTypeValue}
        >
          <span>
            <FormControlLabel
              control={<Radio onChange={handleChangeOrderServiceType} />}
              label={t(
                "orders:orderEntry.summary.header.orderService.delivery"
              )}
              value="DELIVERY"
            />
            <FormControlLabel
              control={<Radio onChange={handleChangeOrderServiceType} />}
              label={t("orders:orderEntry.summary.header.orderService.pickup")}
              value="PICKUP"
            />
          </span>
        </RadioGroup>
      </FormControl>
    );

    const getGeneratedFrom = () => {
      const generatedFrom = order.generatedFrom as string;
      return generatedByJDPoint ? (
        <ResourceLink
          key="generatedFrom"
          value={generatedFrom}
          url={createJDPointOrderLink(
            JDPOINT_URL,
            generatedFrom,
            btoa(accountId as string)
          )}
        />
      ) : (
        generatedFrom
      );
    };

    const getOrderTypeHeaderValue = () =>
      t(`orders:orderType.${order.orderType}`);
    const isTheClientUI = () => order.client === "UI" || !order.client;

    const getLastChangedByHeaderValue = () =>
      isTheClientUI() ? order.lastUpdatedBy ?? "NA" : order.client ?? "NA";

    const getSubmittedByHeaderValue = () =>
      !orderSubmitted || isTheClientUI()
        ? order.submittedBy ?? "NA"
        : order.client ?? "NA";

    const getOrderServiceHeaderValue = () => {
      if (!isOrderServiceEnabled) return null;
      return (
        <Box data-testid="order-service-type-value">
          {isEntryPage
            ? getDeliveryOptionRadioGroup()
            : t(
                `orders:orderEntry.summary.header.orderService.${order.orderServiceType.toLowerCase()}`
              )}
        </Box>
      );
    };
    const getOrderConsolidationHeaderValue = () =>
      isOrderConsolidationEnabled ? getOrderConsolidationToggleSwitch() : null;
    const getDealerReferenceHeaderValue = () =>
      orderSubmitted && (order.dealerReference ?? " ");
    const getGeneratedFromHeaderValue = () =>
      externalOrderNumberPresent ? getGeneratedFrom() : null;
    const getDiscountHeaderValue = () => {
      if (!isReviewAndSubmitPage) return null;
      return order.discountDescription === ""
        ? "None"
        : order.discountDescription;
    };

    const headers = [
      {
        name: t("orders:orderEntry.summary.header.orderType"),
        dataTestId: "order-type-header",
        value: getOrderTypeHeaderValue(),
      },
      {
        name: t("orders:orderEntry.summary.header.lastChangedBy"),
        dataTestId: "last-changed-by-header",
        value: getLastChangedByHeaderValue(),
      },
      {
        name: t("orders:orderEntry.summary.header.submittedBy"),
        dataTestId: "submitted-by-header",
        value: getSubmittedByHeaderValue(),
      },
      {
        name: t("orders:orderEntry.summary.header.orderService"),
        dataTestId: "order-service-header",
        value: getOrderServiceHeaderValue(),
      },
      {
        name: t("orders:orderEntry.summary.header.consolidateOrder"),
        dataTestId: "order-consolidation-header",
        value: getOrderConsolidationHeaderValue(),
      },
      {
        name: t("orders:orderEntry.summary.header.dealerReference"),
        dataTestId: "dealer-reference-header",
        value: getDealerReferenceHeaderValue(),
      },
      {
        name: t("orders:orderSummary.header.generatedFrom"),
        dataTestId: "order-generated-from",
        value: getGeneratedFromHeaderValue(),
      },
      {
        name: "Discount",
        dataTestId: "selected-discount-header",
        value: getDiscountHeaderValue(),
      },
    ];

    const activeHeaders = headers.filter((header) => header.value);

    const calculateGridSize = (activeHeadersLength: number) => {
      if (
        showPromotionOptions &&
        isOrderNotSubmittedAndInReviewPage &&
        activeHeadersLength < MIN_COLS_IN_PROMOTION_ORDER
      ) {
        return 12 / (activeHeadersLength + 1);
      }
      return activeHeadersLength < 6 ? 12 / activeHeadersLength : 2;
    };
    const gridSize = calculateGridSize(activeHeaders.length);

    const dealerAccountNumber = () => (
      <Box>
        <Box
          sx={headingSx(theme)}
          data-testid="order-summary-dealer-account-header"
        >
          {t("orders:orderEntry.summary.header.dealerAccountNumber")}
        </Box>
        <Box data-testid="order-summary-dealer-account-value">
          {dealerAccountId}
        </Box>
      </Box>
    );
    const dealerReferenceHeader = () => (
      <Box
        data-testid="dealer-reference-input-block"
        sx={{
          maxWidth:
            showPromotionOptions && isOrderNotSubmittedAndInReviewPage
              ? "85%"
              : "100%",
        }}
      >
        <Box sx={headingSx(theme)}>
          {t("orders:orderEntry.summary.header.dealerReference")}
        </Box>
        <Box>{getDealerReferenceField()}</Box>
      </Box>
    );

    const getOrderCommentModel = (
      value: string,
      openModal: boolean,
      onClose: () => void,
      onAddComments: (comments: string) => void
    ) => (
      <Dialog
        aria-describedby="alert-Modal-add-comments"
        aria-labelledby="alert-Modal-comments"
        open={openModal}
        maxWidth="lg"
        data-testid="order-comment-modal"
        sx={{
          "& [role='dialog']": {
            width: "50%",
          },
        }}
      >
        <DialogTitle data-testid="comment-Modal-title">
          {t("orders:orderEntry.summary.header.comments")}
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={onClose}
          size="large"
          sx={{
            position: "absolute",
            right: 15,
            top: 15,
          }}
        >
          <Close />
        </IconButton>
        <DialogContent>
          <TextField
            id="order-comment-input"
            inputProps={{ "data-testid": "order-comment-input-field" }}
            helperText={`${comments.length}/${orderNotesMaxLength}`}
            rows={2}
            multiline
            placeholder={
              t("orders:orderEntry.summary.header.comments.placeholder") ?? ""
            }
            type="text"
            sx={{ pt: theme.tokens.FuelSpacing1 }}
            value={comments}
            onChange={(event) =>
              setComments(event.target.value.slice(0, orderNotesMaxLength))
            }
          />
        </DialogContent>
        <Divider
          sx={{
            borderWidth: "1px",
            borderColor: `${theme.tokens.FuelColorBorderLight}!important`,
          }}
          orientation="vertical"
        />
        <DialogActions>
          <Button
            data-testid="comment-cancel-button"
            variant="text"
            onClick={() => {
              onClose();
              setComments(value);
            }}
          >
            {t("common:modal.cancelBtn")}
          </Button>
          <Button
            data-testid="add-comment-button"
            variant="primary"
            onClick={() => {
              onAddComments(comments);
            }}
          >
            {t("orderEntry.summary.header.addComments")}
          </Button>
        </DialogActions>
      </Dialog>
    );

    const getOrderCommentHeader = () => (
      <Grid container alignItems="center">
        <Grid
          sx={{ ...headingSx(theme), paddingBottom: theme.tokens.FuelSpacing2 }}
          data-testid="order-comment-header"
        >
          {t("orders:orderEntry.summary.header.comments")}
        </Grid>
      </Grid>
    );

    const getCommentGrid = () => {
      const closeCommentBox = () => setCommentOpenState(false);
      const openCommentBox = () => setCommentOpenState(true);
      const commentButtonText = orderNotesValue
        ? t("orders:orderEntry.summary.header.editComments")
        : t("orders:orderEntry.summary.header.addComments");
      const handleAddComment = (text: string) => {
        setOrderNotesValue(text);
        enqueueToast({
          variant: "success",
          message: t(
            "orders:orderEntry.summary.header.comments.AddActionCompleted"
          ),
          isCloseable: true,
        });
        closeCommentBox();
      };

      return (
        <>
          {getOrderCommentHeader()}
          <Button
            sx={{ width: "187px" }}
            startIcon={<AddComment />}
            size="large"
            variant="secondary"
            data-testid="add-comment-button-with-icon"
            onClick={openCommentBox}
          >
            {commentButtonText}
          </Button>
          {getOrderCommentModel(
            orderNotesValue ?? "",
            isOpen,
            closeCommentBox,
            handleAddComment
          )}
        </>
      );
    };

    const getOrderSubmitDate = () => {
      const submitDate = formatDate(
        order.submittedAt?.toISOString(),
        i18n.language
      );
      const submitTime = order.submittedAt?.toTimeString().split(" ")[0];
      return (
        <Typography
          color={theme.tokens.FuelColorTextTertiary}
          data-testid="order-submitted-date"
        >
          {order.submittedAt && `${submitDate} | ${submitTime}`}
        </Typography>
      );
    };

    const orderSummaryHeaderTitle = () => (
      <Grid
        container
        alignItems="center"
        sx={{
          justifyContent: "space-between",
          px: theme.tokens.FuelSpacing4,
        }}
      >
        <Grid>
          <Grid container alignItems="center" gap={theme.tokens.FuelSpacing6}>
            <Grid>
              <Typography
                component="h4"
                data-testid="order-summary-header-title"
                sx={orderSummaryHeaderSx}
              >
                {t("orders:orderEntry.summary.header.orderNumber")}:{" "}
                {order.orderNumber}
              </Typography>
            </Grid>
            <Grid>{order.submittedAt && getOrderSubmitDate()}</Grid>
          </Grid>
        </Grid>
        <Grid>
          {printing ? (
            <Typography data-testid="order-status-in-text">
              {t(`orders:orderStatus.${OrderStatus[order.orderStatus]}`)}
            </Typography>
          ) : (
            <OrderStatusBadge status={order.orderStatus} />
          )}
        </Grid>
      </Grid>
    );
    const orderAddressAndEstimatedCostOnReviewAndSubmitPage = () => {
      if (isOrderNotSubmittedAndInReviewPage || orderSubmitted) {
        return (
          <>
            <Divider />
            <Box display="flex" data-testid="order-address-estimated-cost">
              <DealerAddress
                address={convertAddressToTitleCase(order.shipToAddress)}
                title={t("orders:dealerAddress.shipTo")}
                isCleanserAddressValidationEnabled={
                  isCleanserAddressValidationEnabled
                }
                isV2AddressValidationEnabled={isV2AddressValidationEnabled}
                editable={
                  !isMaskShipToAddressEnabled &&
                  isOrderNotSubmittedAndInReviewPage &&
                  (isCleanserAddressValidationEnabled ||
                    isV2AddressValidationEnabled ||
                    (isAddressOverrideEditEnabled &&
                      !isAddressOverrideSelectionEnabled))
                }
                selectable={
                  isOrderNotSubmittedAndInReviewPage &&
                  isAddressOverrideSelectionEnabled
                }
                disableSelection={
                  !isMaskShipToAddressEnabled &&
                  isOrderNotSubmittedAndInReviewPage &&
                  isAddressOverrideSelectionDisabled
                }
                masked={isMaskShipToAddressEnabled}
                orderNumber={order.orderNumber}
                accountId={accountId}
                updateAddress={updateShipToAddress}
                setWarningEditModel={setWarningEditModel}
              />
              <Divider flexItem orientation="vertical" />
              <DealerAddress
                address={convertAddressToTitleCase(order.soldToAddress)}
                title={t("orders:dealerAddress.soldTo")}
              />
              {orderSubmitted && (
                <>
                  <Divider flexItem orientation="vertical" />
                  <EstimatedCost order={order} />
                </>
              )}
            </Box>
          </>
        );
      }

      return <></>;
    };

    const commentsView = (
      <>
        <Box sx={{ ...headingSx(theme) }} data-testid="order-comment-header">
          {t("orders:orderEntry.summary.header.comments")}
        </Box>
        <Box data-testid="order-comment-value">
          {orderNotesValue?.trim() ? orderNotesValue : ""}
        </Box>
      </>
    );

    const renderCommentsSection = () => {
      const commentsSection = (
        <Grid
          size={{ xs: 3 }}
          p={4}
          borderLeft={1}
          borderColor={theme.tokens.FuelColorBorderLight}
        >
          {getCommentGrid()}
        </Grid>
      );

      if (orderSubmitted)
        return (
          <Grid py={4} paddingLeft={theme.tokens.FuelSpacing4}>
            {commentsView}
          </Grid>
        );

      return commentsSection;
    };

    const renderPromotionEntryOptions = () => {
      const shipFromDate = promotion?.shipFromDate;
      const shipToDate = promotion?.shipToDate;

      return (
        <>
          <Grid size={{ xs: 6 }}>
            <PromotionSelect
              promotions={availablePromotions || []}
              onSelect={updatePromotion}
              selectedValue={promotion}
            />
          </Grid>
          <Grid size={{ xs: 2 }}>
            <RequestedShipMonthSelector
              disabled={promotion === undefined}
              handleOnClick={() => handleOnClickRequestShipMonth()}
              onSelect={updateRequestedShipMonth}
              selectedValue={
                expiredPromotionId?.length === 0 && requestedShipMonth
                  ? dayjs(requestedShipMonth)
                  : undefined
              }
              startPeriod={
                shipFromDate
                  ? {
                      month: getMaxDate(
                        dayjs.utc(),
                        dayjs.utc(shipFromDate)
                      ).month(),
                      year: getMaxDate(
                        dayjs.utc(),
                        dayjs.utc(shipFromDate)
                      ).year(),
                    }
                  : null
              }
              endPeriod={
                shipToDate
                  ? {
                      month: dayjs.utc(shipToDate).month(),
                      year: dayjs.utc(shipToDate).year(),
                    }
                  : null
              }
              showRequestShipMonthWarning={showRequestShipMonthWarning}
            />
          </Grid>
        </>
      );
    };

    const renderPromotionReadonlyOptions = () => (
      <>
        <Grid size={{ xs: gridSize }}>
          <ReadonlyPromotionIdBlock promotionId={order.promotion!.id} />
        </Grid>
        <Grid size={{ xs: gridSize }}>
          <ReadonlyPromotionDescriptionBlock
            promotionDescription={order.promotion!.description}
          />
        </Grid>
        <Grid size={{ xs: gridSize }}>
          <ReadonlyRequestedShipMonthBlock
            requestedShipMonth={order.requestedShipMonth as Dayjs}
          />
        </Grid>
      </>
    );

    const getDiscount = () => {
      if (applicableDiscounts.length === 0) return <></>;

      const discountOptions = applicableDiscounts.map(
        ({ discount, description }) => (
          <Option key={discount} value={discount}>
            {description || t("orderEntry.summary.header.discount.none")}
          </Option>
        )
      );

      return (
        <Box data-testid="dealer-selection-discount">
          <Typography
            data-testid="dealer-selection-discount-header"
            sx={headingSx(theme)}
          >
            {t("orders:orderEntry.summary.header.discount")}
          </Typography>
          <Select
            defaultValue={dealerSelectedDiscount.discount}
            disabled={!isEntryPage}
            onChange={(e) => {
              setDealerSelectedDiscount({
                discount: e.target.value,
                description: getDiscountDescription(e.target.value),
              });
            }}
          >
            {discountOptions}
          </Select>
        </Box>
      );
    };

    const getDealerReferenceAndDiscount = () => (
      <Grid size={{ xs: 9 }} px={4}>
        <Grid container spacing={4}>
          {showDealerAccountNumber && (
            <Grid size={{ xs: 3 }}>{dealerAccountNumber()}</Grid>
          )}
          <Grid size={{ xs: 6 }}>{dealerReferenceHeader()}</Grid>
          <Grid size={{ xs: 3 }}>{getDiscount()}</Grid>
        </Grid>
      </Grid>
    );

    const orderSummaryLineTwoForPromotionOrder = () => (
      <Grid
        container
        spacing={4}
        padding={4}
        data-testid="promotion-order-summary-line-two"
      >
        {showPromotionEntryOptions && renderPromotionEntryOptions()}
        {showPromotionReadonlyOptions && renderPromotionReadonlyOptions()}
        {!orderSubmitted && (
          <Grid size={{ xs: (step ?? 0) > 0 ? 3 : 4 }}>
            {dealerReferenceHeader()}
          </Grid>
        )}
      </Grid>
    );

    const orderSummaryLineTwo = () => (
      <Grid
        container
        data-testid="order-summary-header-line-two"
        alignItems="center"
      >
        {!orderSubmitted && getDealerReferenceAndDiscount()}
        {renderCommentsSection()}
      </Grid>
    );

    const orderSummaryLineThree = () => (
      <Grid
        container
        sx={{ px: 4, pb: 4, flexDirection: "column" }}
        data-testid="order-summary-header-line-three"
      >
        {orderSubmitted ? <Grid>{commentsView}</Grid> : getCommentGrid()}
      </Grid>
    );

    const isDividerVisible =
      order.orderType !== OrderType.PROMOTION_ORDER ||
      step === 0 ||
      (step === 1 && showPromotionOptions);

    return (
      <>
        <Box data-testid="order-summary-header" sx={ordersSummaryContainerSx}>
          {orderSummaryHeaderTitle()}
          <Divider sx={{ my: theme.tokens.FuelSpacing4 }} />
          <OrderSummaryHeaderLineOne
            gridSize={gridSize}
            activeHeaders={activeHeaders}
          />
          {isDividerVisible && (
            <Divider sx={{ mt: theme.tokens.FuelSpacing4 }} />
          )}
          {showPromotionOptions
            ? orderSummaryLineTwoForPromotionOrder()
            : orderSummaryLineTwo()}
          {showPromotionOptions && (
            <>
              <Divider sx={{ mb: theme.tokens.FuelSpacing4 }} />
              {orderSummaryLineThree()}
            </>
          )}
          {orderAddressAndEstimatedCostOnReviewAndSubmitPage()}
        </Box>
        <InvalidAddressDialog
          isOpen={isInvalidAddressDialogOpen}
          close={() => setIsInvalidAddressDialogOpen(false)}
        />
      </>
    );
  }
);

export default OrderHeader;
